import { useEntitlement } from '@features/entitlements';
import { useFeatureGate } from '@services/statsig';

interface JotBillAccess {
  /** Whether the user has JotBill access (entitlement present OR Statsig gate). False while loading. */
  hasAccess: boolean;
  /** Whether the entitlement data is still loading */
  isLoading: boolean;
}

/**
 * Source of truth for JotBill access in the web app.
 *
 * Modeled on `useTelehealthAccess`, but access is granted on the `jotbill`
 * entitlement OR the existing `jotbill` Statsig gate. The OR keeps the swap
 * low-risk during rollout:
 *
 *   1. Orgs currently enabled via the Statsig gate may not have a
 *      `jotbill_managed` bundle assignment yet.
 *   2. `useEntitlement` reports no entitlement for orgs not on the bundling
 *      system at all — for those, only the gate can answer.
 *
 * Removing the Statsig gate is a later cleanup once every managed-billing org
 * has the bundle assigned.
 */
export function useJotBillAccess(): JotBillAccess {
  const { entitlement, isLoading } = useEntitlement('jotbill');
  const featureGate = useFeatureGate('jotbill');

  // Still loading — default to hidden to prevent flash.
  if (isLoading) {
    return { hasAccess: false, isLoading: true };
  }

  // Entitlement present OR the existing Statsig gate grants access.
  return { hasAccess: !!entitlement || featureGate, isLoading: false };
}
